import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Plus, Trash2, ToggleLeft, ToggleRight, Edit2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

const BookingEditor = ({ profile, setProfile }) => {
  const [enabled, setEnabled] = useState(false);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState({});
  const [slotDuration, setSlotDuration] = useState(30);
  const [newService, setNewService] = useState({ name: '', duration: 30, price: '' });
  const [saving, setSaving] = useState(false);
  const [editingDay, setEditingDay] = useState(null);

  useEffect(() => {
    if (profile) {
      setEnabled(profile.booking_enabled || false);
      setServices(profile.booking_services || []);
      setSlotDuration(profile.booking_slot_duration || 30);
      const avail = profile.booking_availability || {};
      const initial = {};
      DAYS.forEach(d => {
        initial[d] = avail[d] || { enabled: false, mode: 'range', start: '09:00', end: '17:00', slots: [] };
      });
      setAvailability(initial);
    }
  }, [profile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/profile`, {
        booking_enabled: enabled,
        booking_services: services,
        booking_availability: availability,
        booking_slot_duration: slotDuration,
      });
      setProfile(res.data);
      toast.success('Booking settings saved');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addService = () => {
    if (!newService.name.trim()) return;
    const svc = {
      id: 'svc_' + Date.now(),
      name: newService.name.trim(),
      duration: parseInt(newService.duration) || 30,
      price: newService.price.trim() || null,
    };
    setServices(prev => [...prev, svc]);
    setNewService({ name: '', duration: 30, price: '' });
  };

  const removeService = (id) => setServices(prev => prev.filter(s => s.id !== id));

  const toggleDay = (day) => {
    setAvailability(prev => ({
      ...prev,
      [day]: { ...prev[day], enabled: !prev[day]?.enabled }
    }));
  };

  const updateDay = (day, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  const addSlotToDay = (day) => {
    const current = availability[day]?.slots || [];
    setAvailability(prev => ({
      ...prev,
      [day]: { ...prev[day], slots: [...current, '09:00'] }
    }));
  };

  const updateSlot = (day, idx, value) => {
    const current = [...(availability[day]?.slots || [])];
    current[idx] = value;
    setAvailability(prev => ({ ...prev, [day]: { ...prev[day], slots: current } }));
  };

  const removeSlot = (day, idx) => {
    const current = [...(availability[day]?.slots || [])];
    current.splice(idx, 1);
    setAvailability(prev => ({ ...prev, [day]: { ...prev[day], slots: current } }));
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="font-outfit font-bold text-2xl text-white">Booking System</h1>
        <p className="text-[#A1A1AA] mt-1">Let visitors book appointments with you</p>
      </div>

      <div className="space-y-6">
        {/* Enable Toggle */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="font-outfit font-semibold text-lg text-white">Enable Bookings</h2>
                <p className="text-sm text-[#A1A1AA]">Show "Book Appointment" on your profile</p>
              </div>
            </div>
            <Switch data-testid="booking-toggle" checked={enabled} onCheckedChange={setEnabled} />
          </div>
        </motion.div>

        {enabled && (
          <>
            {/* Services */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
              <h2 className="font-outfit font-semibold text-lg text-white mb-1">Services</h2>
              <p className="text-sm text-[#71717A] mb-4">Define what visitors can book</p>

              <div className="space-y-2 mb-4">
                {services.map(svc => (
                  <div key={svc.id} data-testid={`service-${svc.id}`} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <span className="text-white text-sm font-medium">{svc.name}</span>
                      <span className="text-[#71717A] text-xs ml-2">{svc.duration} min</span>
                      {svc.price && <span className="text-violet-400 text-xs ml-2">{svc.price}</span>}
                    </div>
                    <button data-testid={`remove-service-${svc.id}`} onClick={() => removeService(svc.id)} className="text-red-400 hover:text-red-300">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {services.length === 0 && <p className="text-[#525252] text-sm text-center py-4">No services yet. Add one below.</p>}
              </div>

              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs text-[#A1A1AA]">Service Name</Label>
                  <Input data-testid="service-name-input" value={newService.name} onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && addService()} placeholder="e.g. Consultation" className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white text-sm" />
                </div>
                <div className="w-24">
                  <Label className="text-xs text-[#A1A1AA]">Duration</Label>
                  <Input data-testid="service-duration-input" type="number" value={newService.duration} onChange={(e) => setNewService({ ...newService, duration: e.target.value })}
                    placeholder="30" className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white text-sm" min={5} max={480} />
                </div>
                <div className="w-24">
                  <Label className="text-xs text-[#A1A1AA]">Price</Label>
                  <Input data-testid="service-price-input" value={newService.price} onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                    placeholder="Free" className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white text-sm" />
                </div>
                <Button data-testid="add-service-btn" onClick={addService} size="sm" className="bg-violet-500 hover:bg-violet-600 text-white rounded-lg shrink-0 mb-0.5">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>

            {/* Slot Duration */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
              <h2 className="font-outfit font-semibold text-lg text-white mb-1">Slot Duration</h2>
              <p className="text-sm text-[#71717A] mb-4">Default time slot length for auto-generated slots</p>
              <div className="flex gap-2">
                {[15, 30, 45, 60, 90].map(d => (
                  <button key={d} data-testid={`slot-duration-${d}`} onClick={() => setSlotDuration(d)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${slotDuration === d ? 'bg-violet-500 text-white' : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'}`}>
                    {d} min
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Availability */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
              <h2 className="font-outfit font-semibold text-lg text-white mb-1">Availability</h2>
              <p className="text-sm text-[#71717A] mb-4">Set your available days and times</p>

              <div className="space-y-3">
                {DAYS.map(day => {
                  const config = availability[day] || { enabled: false, mode: 'range', start: '09:00', end: '17:00', slots: [] };
                  return (
                    <div key={day} className="rounded-xl bg-white/[0.03] border border-white/[0.05] overflow-hidden">
                      <div className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-3">
                          <button data-testid={`toggle-day-${day}`} onClick={() => toggleDay(day)}>
                            {config.enabled ? <ToggleRight className="w-7 h-7 text-violet-400" /> : <ToggleLeft className="w-7 h-7 text-[#555]" />}
                          </button>
                          <span className={`text-sm font-medium capitalize ${config.enabled ? 'text-white' : 'text-[#555]'}`}>{day}</span>
                        </div>
                        {config.enabled && (
                          <div className="flex items-center gap-2">
                            <button onClick={() => updateDay(day, 'mode', config.mode === 'range' ? 'slots' : 'range')}
                              className="text-xs px-2 py-1 rounded bg-white/5 text-[#A1A1AA] hover:bg-white/10">
                              {config.mode === 'range' ? 'Time Range' : 'Custom Slots'}
                            </button>
                          </div>
                        )}
                      </div>

                      {config.enabled && (
                        <div className="px-3 pb-3">
                          {config.mode === 'range' ? (
                            <div className="flex items-center gap-2">
                              <Input data-testid={`${day}-start`} type="time" value={config.start || '09:00'} onChange={(e) => updateDay(day, 'start', e.target.value)}
                                className="w-32 bg-[#1E1E1E] border-[#27272A] text-white text-sm" />
                              <span className="text-[#71717A] text-sm">to</span>
                              <Input data-testid={`${day}-end`} type="time" value={config.end || '17:00'} onChange={(e) => updateDay(day, 'end', e.target.value)}
                                className="w-32 bg-[#1E1E1E] border-[#27272A] text-white text-sm" />
                            </div>
                          ) : (
                            <div>
                              <div className="flex flex-wrap gap-2 mb-2">
                                {(config.slots || []).map((slot, idx) => (
                                  <div key={idx} className="flex items-center gap-1 bg-white/5 rounded-lg px-2 py-1">
                                    <Input type="time" value={slot} onChange={(e) => updateSlot(day, idx, e.target.value)}
                                      className="w-28 bg-transparent border-0 text-white text-xs p-0 h-auto focus-visible:ring-0" />
                                    <button onClick={() => removeSlot(day, idx)} className="text-red-400 hover:text-red-300">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <button onClick={() => addSlotToDay(day)} className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1">
                                <Plus className="w-3 h-3" /> Add slot
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}

        <Button data-testid="save-booking-settings" onClick={handleSave} disabled={saving} className="w-full rounded-full bg-violet-500 hover:bg-violet-600 text-white">
          {saving ? 'Saving...' : 'Save Booking Settings'}
        </Button>
      </div>
    </div>
  );
};

export default BookingEditor;
